import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { CarFrontIcon, CheckIcon, ChevronsUpDownIcon, LayersIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useCurrentVehicle } from "@/lib/current-vehicle";
import { useTRPC } from "@/lib/trpc";
import { cn } from "@/lib/utils";
import { ChassisCodeChip } from "./chassis-codes";

/**
 * Picks the current vehicle (ADR 0008): a full-width control (sidebar, work orders) or, on phones,
 * an icon button in the top bar.
 */
export function CurrentVehicleSelect({
  variant = "field",
  className,
}: {
  variant?: "field" | "icon";
  className?: string;
}) {
  const { t } = useTranslation();
  const trpc = useTRPC();
  const { data: vehicles = [] } = useQuery(trpc.vehicles.list.queryOptions());
  const { vehicle, setVehicleId } = useCurrentVehicle();
  const label = vehicle
    ? t("currentVehicle.label", { vehicle: vehicle.name })
    : t("currentVehicle.labelAll");
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {variant === "icon" ? (
          <button
            type="button"
            aria-label={label}
            className={cn(
              "relative flex size-9 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground",
              vehicle && "text-brand",
              className,
            )}
          >
            <CarFrontIcon className="size-5" />
            {vehicle ? (
              <span className="absolute top-1.5 right-1.5 size-2 rounded-full bg-brand ring-2 ring-background" />
            ) : null}
          </button>
        ) : (
          <Button
            variant="outline"
            aria-label={label}
            className={cn("w-full justify-between gap-2 px-2.5 font-medium", className)}
          >
            <span className="flex min-w-0 items-center gap-2">
              <CarFrontIcon className={cn(vehicle ? "text-brand" : "text-muted-foreground")} />
              <span className="truncate">{vehicle ? vehicle.name : t("currentVehicle.all")}</span>
              {vehicle?.chassisCode ? <ChassisCodeChip code={vehicle.chassisCode} /> : null}
            </span>
            <ChevronsUpDownIcon className="text-muted-foreground" />
          </Button>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64 max-w-[calc(100vw-2rem)]">
        <DropdownMenuLabel>{t("currentVehicle.title")}</DropdownMenuLabel>
        <DropdownMenuItem onSelect={() => setVehicleId(null)}>
          <CheckIcon className={cn(vehicle ? "opacity-0" : "opacity-100")} />
          <LayersIcon />
          {t("currentVehicle.all")}
        </DropdownMenuItem>
        {vehicles.length ? <DropdownMenuSeparator /> : null}
        {vehicles.map((v) => (
          <DropdownMenuItem key={v.id} onSelect={() => setVehicleId(v.id)}>
            <CheckIcon className={cn(v.id === vehicle?.id ? "opacity-100" : "opacity-0")} />
            <span className="min-w-0 flex-1 truncate">{v.name}</span>
            {v.chassisCode ? <ChassisCodeChip code={v.chassisCode} /> : null}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Below a page title: says the page shows only what is relevant to the current vehicle. */
export function CurrentVehicleNotice({ className }: { className?: string }) {
  const { t } = useTranslation();
  const { vehicle, setVehicleId } = useCurrentVehicle();
  if (!vehicle) return null;
  return (
    <div
      role="status"
      className={cn(
        "mb-5 flex flex-col gap-3 rounded-xl border border-brand/25 bg-brand-soft px-4 py-3 text-sm text-brand-fg sm:flex-row sm:items-center sm:justify-between",
        className,
      )}
    >
      <p className="flex items-start gap-2">
        <CarFrontIcon className="mt-0.5 size-4 shrink-0" aria-hidden />
        <span>
          {vehicle.chassisCode ? (
            t("currentVehicle.notice", { vehicle: vehicle.name, code: vehicle.chassisCode.name })
          ) : (
            <>
              {t("currentVehicle.noticeNoCode", { vehicle: vehicle.name })}{" "}
              <Link
                to="/vehicles/$vehicleId"
                params={{ vehicleId: vehicle.id }}
                className="font-medium underline underline-offset-2"
              >
                {t("currentVehicle.setChassisCode")}
              </Link>
            </>
          )}
        </span>
      </p>
      <Button
        variant="outline"
        size="sm"
        className="shrink-0 self-start sm:self-auto"
        onClick={() => setVehicleId(null)}
      >
        {t("currentVehicle.showAll")}
      </Button>
    </div>
  );
}
